import {
  EnvironmentProviders,
  InjectionToken,
  makeEnvironmentProviders,
} from '@angular/core';

/**
 * Runtime configuration for the payments feature.
 *
 * In ZIM-Portal these values were split between a runtime `ConfigService`
 * (vault config) and the app `environment`. To keep this library
 * app-agnostic we collapse them into a single injectable config supplied by
 * the host application via {@link providePayments}.
 */
export interface PaymentsConfig {
  /** Backend API base url, e.g. `https://api.../deeds/`. Must end with `/`. */
  apiUrl: string;
  /** Frontend base url used as the default payment callback. */
  frontendUrl: string;
  /** Host app name — drives Deeds-specific behaviour (e.g. `'Deeds'`). */
  appName: string;
  /** Optional portal identifier (`'Repz'` unlocks subscription hints). */
  appPortal?: string;
  /** Whether the host app is running in production (affects min amounts). */
  production: boolean;

  // iVeri (Visa & Mastercard)
  iveriApplicationId: string;
  iveriGateway: string;
  iveriReturnUrl: string;

  // Zimswitch (OPPWA widget)
  zimswitchGateway: string;
  paymentRedirectUrl: string;

  /** Links shown in the guest personal-information / sign-in panel. */
  checkoutAuthInfo?: {
    signInUrl?: string;
    signUpUrl?: string;
    googleSignUrl?: string;
  };

  /**
   * localStorage key the logged-in user is read from. Defaults to
   * `auth_user`, which is what `TokenStorageService` writes the session
   * under. ZIM-Portal used `user`; nothing in this workspace does.
   */
  userStorageKey?: string;
}

export const PAYMENTS_CONFIG = new InjectionToken<PaymentsConfig>(
  'PAYMENTS_CONFIG'
);

const DEFAULTS: Partial<PaymentsConfig> = {
  userStorageKey: 'auth_user',
  appPortal: '',
};

/**
 * Registers the payments configuration for the whole app.
 *
 * @example
 * providers: [
 *   providePayments({
 *     apiUrl: environment.server,
 *     frontendUrl: window.location.origin + '/',
 *     appName: 'Deeds',
 *     production: environment.production,
 *     iveriApplicationId: '...',
 *     iveriGateway: '...',
 *     iveriReturnUrl: '...',
 *     zimswitchGateway: '...',
 *     paymentRedirectUrl: '...',
 *   }),
 * ]
 */
export function providePayments(config: PaymentsConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: PAYMENTS_CONFIG, useValue: { ...DEFAULTS, ...config } },
  ]);
}
